import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Edit, Trash2, Star, Phone, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function LocationManager() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', is_primary: false });

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await api.get('/api/locations');
      setLocations(data);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.state || !form.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/api/locations/${editId}`, form);
        toast.success('Location updated');
      } else {
        await api.post('/api/locations', form);
        toast.success('Location added!');
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', is_primary: false });
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (loc) => {
    setForm({ name: loc.name, address: loc.address, city: loc.city, state: loc.state, zip_code: loc.zip_code, phone: loc.phone || '', is_primary: loc.is_primary });
    setEditId(loc.id);
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/locations/${id}`);
      toast.success('Location deleted');
      fetchLocations();
    } catch (err) {
      toast.error('Failed to delete location');
    }
  };

  if (loading) {
    return <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />;
  }

  return (
    <div className="max-w-2xl" data-testid="location-manager">
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <MapPin className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-xl">Lot Locations</h3>
              <p className="text-gray-500">Manage your dealership locations</p>
            </div>
          </div>
          <Button className="rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white" onClick={() => { setEditId(null); setForm({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', is_primary: false }); setShowAdd(true); }} data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Location
          </Button>
        </div>

        {locations.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No locations added yet. Add your first lot!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {locations.map(loc => (
              <div key={loc.id} className={`p-4 rounded-xl border transition-all ${loc.is_primary ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-100'}`} data-testid={`location-card-${loc.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{loc.name}</h4>
                      {loc.is_primary && (
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium flex items-center gap-1">
                          <Star className="w-3 h-3" /> Primary
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{loc.address}</p>
                    <p className="text-sm text-gray-500">{loc.city}, {loc.state} {loc.zip_code}</p>
                    {loc.phone && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Phone className="w-3 h-3" /> {loc.phone}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(loc)} className="p-2 rounded-lg hover:bg-white text-gray-400 hover:text-indigo-600 transition-colors" data-testid={`edit-location-${loc.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(loc.id)} className="p-2 rounded-lg hover:bg-white text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-location-${loc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{editId ? 'Edit Location' : 'Add Location'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div>
              <Label className="text-gray-700 font-medium">Location Name *</Label>
              <Input placeholder="Main Lot" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="location-name" />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Address *</Label>
              <Input placeholder="123 Auto Drive" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} data-testid="location-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-700 font-medium">City *</Label>
                <Input placeholder="Dallas" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid="location-city" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">State *</Label>
                <Input placeholder="TX" maxLength={2} value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value.toUpperCase() }))} data-testid="location-state" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">ZIP *</Label>
                <Input placeholder="75201" value={form.zip_code} onChange={e => setForm(p => ({ ...p, zip_code: e.target.value }))} data-testid="location-zip" />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Phone</Label>
              <Input placeholder="555-0101" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="location-phone" />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">Primary Location</p>
                <p className="text-xs text-gray-500">Set as your main dealership lot</p>
              </div>
              <Switch checked={form.is_primary} onCheckedChange={v => setForm(p => ({ ...p, is_primary: v }))} data-testid="location-primary-switch" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-xl h-11 bg-gradient-to-r from-teal-500 to-cyan-500 text-white" data-testid="save-location-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} {editId ? 'Update' : 'Add'} Location
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
